import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { Alert, Button, Segmented, Table, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'

import { HIGH_VALUE_BASKET_LEADS_QUERY } from './graphql'
import { daysSince, formatBaht } from './format'

const PAGE_SIZE = 50
const DANGER_COLOR = '#cf1322'
const STATUS_COLORS = { open: 'processing', won: 'success', lost: 'default' }

export const STATUSES = ['needs_follow_up', 'open', 'won', 'lost', 'all']

const countForStatus = (row, status) => ({
  needs_follow_up: row.needsFollowUpCount,
  open: row.openCount,
  won: row.wonCount,
  lost: row.lostCount,
  all: row.totalCount
}[status])

const LeadsTable = ({ assigneeId, countsRow, minGrandTotal, month, onSelectStatus, status }) => {
  const { i18n } = useTranslation('highValueBaskets')
  const t = (key, options) => i18n.t(`highValueBaskets:${key}`, options)

  const { loading, data, error, fetchMore } = useQuery(HIGH_VALUE_BASKET_LEADS_QUERY, {
    variables: { month, minGrandTotal, status, assigneeId, first: PAGE_SIZE },
    fetchPolicy: 'cache-and-network'
  })

  const connection = (data && data.highValueBasketLeads) || null
  const nodes = (connection && connection.nodes) || []

  const onLoadMore = () => {
    fetchMore({
      variables: { after: connection.pageInfo.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous

        return {
          highValueBasketLeads: {
            ...fetchMoreResult.highValueBasketLeads,
            nodes: [...previous.highValueBasketLeads.nodes, ...fetchMoreResult.highValueBasketLeads.nodes]
          }
        }
      }
    })
  }

  const renderAgo = (isoDateTime) => {
    const days = daysSince(isoDateTime)
    return days === 0 ? t('leads.today') : t('leads.daysAgo', { n: days })
  }

  const columns = [
    {
      key: 'number',
      title: t('leads.columns.number'),
      render: lead => <Link to={`/inquiries/${lead.orderNumber}`}>{lead.orderNumber}</Link>
    },
    {
      key: 'customer',
      title: t('leads.columns.customer'),
      render: lead => lead.customerName || '-'
    },
    {
      key: 'grandTotal',
      align: 'right',
      title: t('leads.columns.grandTotal'),
      render: lead => <strong>{formatBaht(lead.grandTotal)}</strong>
    },
    {
      key: 'state',
      title: t('leads.columns.state'),
      render: lead => <Tag color={STATUS_COLORS[lead.status]}>{lead.stateLabel}</Tag>
    },
    {
      key: 'assignee',
      title: t('leads.columns.assignee'),
      render: lead => (lead.assignee ? lead.assignee.fullName : <Tag color="red">{t('assignees.unassigned')}</Tag>)
    },
    {
      key: 'assignDate',
      title: t('leads.columns.assignDate'),
      render: lead => (lead.assignDate ? (
        <Tooltip title={dayjs(lead.assignDate).format('YYYY-MM-DD HH:mm')}>{renderAgo(lead.assignDate)}</Tooltip>
      ) : '-')
    },
    {
      key: 'lastActivity',
      title: t('leads.columns.lastActivity'),
      render: lead => (
        <Tooltip title={dayjs(lead.lastActivityAt).format('YYYY-MM-DD HH:mm')}>
          <span style={lead.needsFollowUp ? { color: DANGER_COLOR, fontWeight: 600 } : undefined}>
            {renderAgo(lead.lastActivityAt)}
          </span>
        </Tooltip>
      )
    },
    {
      key: 'quotationViewed',
      title: t('leads.columns.quotationViewed'),
      render: lead => (lead.quotationViewedAt ? (
        <Tooltip title={dayjs(lead.quotationViewedAt).format('YYYY-MM-DD HH:mm')}>
          <Tag color="gold">{renderAgo(lead.quotationViewedAt)}</Tag>
        </Tooltip>
      ) : '-')
    },
    {
      key: 'notes',
      align: 'right',
      title: t('leads.columns.notes'),
      dataIndex: 'customerNotesCount'
    }
  ]

  return (
    <>
      <Segmented
        onChange={onSelectStatus}
        options={STATUSES.map(value => ({
          value,
          label: `${t(`leads.statuses.${value}`)} (${countsRow ? countForStatus(countsRow, value) : 0})`
        }))}
        style={{ marginBottom: 16 }}
        value={status}
      />
      {error && <Alert message={error.message} showIcon style={{ marginBottom: 16 }} type="error" />}
      <Table
        columns={columns}
        dataSource={nodes}
        loading={loading}
        locale={{ emptyText: t('leads.empty') }}
        pagination={false}
        rowKey="id"
        scroll={{ x: true }}
        size="small"
      />
      {connection && connection.pageInfo.hasNextPage && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button loading={loading} onClick={onLoadMore}>
            {t('leads.loadMore', { shown: nodes.length })}
          </Button>
        </div>
      )}
    </>
  )
}

LeadsTable.propTypes = {
  assigneeId: PropTypes.string,
  countsRow: PropTypes.object,
  minGrandTotal: PropTypes.number.isRequired,
  month: PropTypes.string.isRequired,
  onSelectStatus: PropTypes.func.isRequired,
  status: PropTypes.oneOf(STATUSES).isRequired
}

LeadsTable.defaultProps = {
  assigneeId: null,
  countsRow: null
}

export default LeadsTable
